import { UnitPriceTier } from '@inosio/payments-sdk';

/**
 * Calculates the cost based on the provided unit tiers and the number of units.
 *
 * This function takes an array of pricing tiers and determines the cost for a given number
 * of units by finding the most applicable tier. Each tier specifies a minimum unit count
 * (`UnitFloor`) and a corresponding cost (`FlatFee`). The function ensures the correct tier
 * is applied by sorting tiers and selecting the highest applicable one.
 *
 * @param tiers - An array of pricing tiers, each containing:
 *   - `UnitFloor`: The minimum number of units required to qualify for this tier.
 *   - `FlatFee`: The cost associated with this tier.
 * @param units - The number of units for which the cost needs to be calculated.
 *
 * @returns The cost associated with the most applicable tier. Returns 0 if no tiers are provided or units are zero.
 *
 * @example
 * const tiers = [
 *   { UnitFloor: 1, FlatFee: 100 },
 *   { UnitFloor: 10, FlatFee: 80 },
 *   { UnitFloor: 20, FlatFee: 60 }
 * ];
 *
 * const units = 15;
 * const cost = calculateCostFromTiers(tiers, units); // Returns 80
 */
export const calculateCostFromTiers = (tiers: UnitPriceTier[], units: number): number => {
  if (!tiers || units === 0) return 0;

  // Step 1: Sort tiers by UnitFloor in ascending order
  const sortedTiers = tiers.slice().sort((a, b) => a.UnitFloor - b.UnitFloor);

  // Step 2: Find the highest applicable tier for the given units
  let applicableTier = sortedTiers[0]; // Start with the first tier
  for (const tier of sortedTiers) {
    if (units < tier.UnitFloor) break; // Stop if the next tier's UnitFloor is greater than units
    applicableTier = tier; // Move to the next applicable tier
  }

  return applicableTier.FlatFee;
};
